import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { API_URL } from '../services/api';

// Formater un montant pour jsPDF (pas de caractères spéciaux, espaces normaux)
const fmtPrix = (n) => {
  const val = parseFloat(n || 0);
  const parts = val.toFixed(2).split('.');
  // Ajouter separateur milliers avec espace normal (pas \u00A0)
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return parts.join(',');
};

// Helper pour récupérer le nom entreprise (cache en mémoire)
let _cachedNomEntreprise = null;
export const getNomEntreprise = async () => {
  if (_cachedNomEntreprise) return _cachedNomEntreprise;
  try {
    const r = await fetch(`${API_URL}/parametres/bijouterie`);
    const d = await r.json();
    _cachedNomEntreprise = d?.nom_entreprise || 'Bijouterie';
    return _cachedNomEntreprise;
  } catch { return 'Bijouterie'; }
};

export const generateFacturePDF = async (vente) => {
  const doc = new jsPDF();
  const W = doc.internal.pageSize.getWidth();
  const nomEntreprise = await getNomEntreprise();

  // Palette bijouterie
  const noir   = [25, 25, 30];
  const or     = [185, 155, 60];
  const orClair = [220, 195, 115];
  const gris   = [120, 120, 120];
  const blanc  = [255, 255, 255];
  const fond   = [252, 250, 245];

  const isCaisseNoir = vente.mode_paiement === 'especes_caisse2';
  const dateVente = new Date(vente.date_vente).toLocaleDateString('fr-FR', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  // ─── Fond page crème ───
  doc.setFillColor(...fond);
  doc.rect(0, 0, W, 297, 'F');

  // ─── Bandeau noir en-tete ───
  doc.setFillColor(...noir);
  doc.rect(0, 0, W, 42, 'F');

  // Filet or sous le bandeau
  doc.setFillColor(...or);
  doc.rect(0, 42, W, 1.5, 'F');

  // Nom entreprise
  doc.setTextColor(...blanc);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(nomEntreprise.toUpperCase(), 20, 20);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...orClair);
  doc.text('Joaillerie artisanale', 20, 28);

  // FACTURE en haut a droite
  doc.setTextColor(...blanc);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('FACTURE', W - 20, 20, { align: 'right' });

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...orClair);
  doc.text(`N. ${String(vente.id).padStart(6, '0')}`, W - 20, 28, { align: 'right' });
  doc.text(dateVente, W - 20, 35, { align: 'right' });

  // ─── Caisse noir avertissement ───
  let yStart = 52;
  if (isCaisseNoir) {
    doc.setFillColor(60, 20, 20);
    doc.rect(20, yStart, W - 40, 10, 'F');
    doc.setTextColor(220, 80, 80);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('DOCUMENT NON FISCAL - HORS COMPTABILITE', W / 2, yStart + 6.5, { align: 'center' });
    yStart += 16;
  }

  // ─── Bloc CLIENT | BOUTIQUE ───
  const yBloc = yStart;
  // Client
  doc.setFillColor(...blanc);
  doc.roundedRect(20, yBloc, 80, 36, 2, 2, 'F');
  doc.setDrawColor(...or);
  doc.setLineWidth(0.4);
  doc.roundedRect(20, yBloc, 80, 36, 2, 2, 'S');

  doc.setTextColor(...or);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('CLIENT', 26, yBloc + 8);

  doc.setTextColor(...noir);
  doc.setFontSize(11);
  doc.text(vente.client_nom || 'Client', 26, yBloc + 16);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gris);
  let yClient = yBloc + 22;
  if (vente.client_telephone) { doc.text(vente.client_telephone, 26, yClient); yClient += 5; }
  if (vente.client_email)     { doc.text(vente.client_email, 26, yClient); }

  // Boutique
  doc.setFillColor(...blanc);
  doc.roundedRect(110, yBloc, 80, 36, 2, 2, 'F');
  doc.setDrawColor(...or);
  doc.roundedRect(110, yBloc, 80, 36, 2, 2, 'S');

  doc.setTextColor(...or);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('BOUTIQUE', 116, yBloc + 8);

  doc.setTextColor(...noir);
  doc.setFontSize(11);
  doc.text(vente.boutique_nom || '', 116, yBloc + 16);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gris);
  doc.text(dateVente, 116, yBloc + 22);

  // ─── Tableau article ───
  const yTable = yBloc + 46;
  autoTable(doc, {
    startY: yTable,
    head: [['Description', 'Reference', 'Categorie', 'Montant']],
    body: [[
      vente.bijou_nom || 'Bijou',
      vente.reference || '-',
      vente.categorie || '-',
      `${fmtPrix(vente.prix_vente)} DH`
    ]],
    theme: 'plain',
    headStyles: {
      fillColor: [...noir],
      textColor: [...blanc],
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: { top: 4, bottom: 4, left: 6, right: 6 }
    },
    bodyStyles: {
      fontSize: 10,
      textColor: [...noir],
      cellPadding: { top: 5, bottom: 5, left: 6, right: 6 }
    },
    columnStyles: {
      0: { cellWidth: 70 },
      1: { cellWidth: 40 },
      2: { cellWidth: 35 },
      3: { cellWidth: 35, halign: 'right', fontStyle: 'bold' }
    },
    margin: { left: 20, right: 20 },
    tableLineColor: [230, 225, 215],
    tableLineWidth: 0.2,
  });

  // ─── Total ───
  const yTotal = doc.lastAutoTable.finalY + 8;

  // Filet or
  doc.setDrawColor(...or);
  doc.setLineWidth(0.6);
  doc.line(110, yTotal, W - 20, yTotal);

  // Mode de paiement
  doc.setTextColor(...gris);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  const modePaiement = {
    'especes_caisse1': 'Especes',
    'especes_caisse2': 'Especes',
    'virement': 'Virement bancaire',
    'cheque': 'Cheque',
    'carte': 'Carte bancaire',
    'autre': 'Autre'
  };
  doc.text(`Reglement : ${modePaiement[vente.mode_paiement] || vente.mode_paiement || ''}`, 20, yTotal + 5);

  // Total TTC
  doc.setTextColor(...noir);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('TOTAL TTC', 115, yTotal + 6);

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(`${fmtPrix(vente.prix_vente)} DH`, W - 20, yTotal + 7, { align: 'right' });

  // Filet or sous total
  doc.setDrawColor(...or);
  doc.setLineWidth(0.3);
  doc.line(110, yTotal + 12, W - 20, yTotal + 12);

  // ─── Mentions legales ───
  doc.setTextColor(...gris);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  if (isCaisseNoir) {
    doc.text('Ce document ne constitue pas un justificatif fiscal.', W / 2, yTotal + 22, { align: 'center' });
  } else {
    doc.text('TVA non applicable (Article 29 B du CGI). Conservez ce document comme justificatif.', W / 2, yTotal + 22, { align: 'center' });
  }

  // ─── Pied de page ───
  // Filet or fin
  doc.setDrawColor(...or);
  doc.setLineWidth(0.5);
  doc.line(20, 272, W - 20, 272);

  doc.setTextColor(...or);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Merci pour votre confiance', W / 2, 279, { align: 'center' });

  doc.setTextColor(...gris);
  doc.setFontSize(7);
  doc.text(`${nomEntreprise}`, W / 2, 285, { align: 'center' });
  doc.text(`Facture N. ${String(vente.id).padStart(6, '0')} - ${new Date().toLocaleDateString('fr-FR')}`, W / 2, 290, { align: 'center' });

  // Sauvegarder
  const prefix = isCaisseNoir ? 'FACTURE_NOIR' : 'FACTURE';
  doc.save(`${prefix}_${String(vente.id).padStart(6, '0')}_${(vente.client_nom || 'client').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
};

// Facture Premium — avec toutes les infos bijou connues
export const generateFacturePremiumPDF = (vente, bijou = {}) => {
  const doc = new jsPDF();

  const dark    = [30, 40, 55];
  const gold    = [194, 154, 50];
  const gold2   = [212, 175, 55];
  const accent  = [180, 30, 30];
  const lightBg = [250, 248, 242];

  const isCaisseNoir = vente.mode_paiement === 'especes_caisse2';

  // ─── Fond page ───────────────────────────────────────────────────────────
  doc.setFillColor(...lightBg);
  doc.rect(0, 0, 210, 297, 'F');

  // ─── Bandeau en-tête ─────────────────────────────────────────────────────
  doc.setFillColor(...dark);
  doc.rect(0, 0, 210, 52, 'F');
  doc.setFillColor(...gold);
  doc.rect(0, 52, 210, 2, 'F');

  // Nom bijouterie
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont(undefined, 'bold');
  doc.text('BIJOUTERIE', 105, 18, { align: 'center' });

  doc.setFontSize(11);
  doc.setFont(undefined, 'normal');
  doc.text('FACTURE DE VENTE', 105, 28, { align: 'center' });

  // Numéro + date en-tête
  doc.setFontSize(9);
  doc.setTextColor(...gold2);
  doc.text(`N° ${String(vente.id).padStart(6, '0')}`, 20, 44);
  doc.setTextColor(200, 200, 200);
  doc.text(`Date : ${new Date(vente.date_vente).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}`, 105, 44, { align: 'center' });
  doc.text(`Boutique : ${vente.boutique_nom || ''}`, 190, 44, { align: 'right' });

  if (isCaisseNoir) {
    doc.setFillColor(...accent);
    doc.rect(0, 54, 210, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.text('VENTE HORS COMPTABILITE — DOCUMENT NON FISCAL', 105, 59, { align: 'center' });
  }

  // ─── Bloc CLIENT ─────────────────────────────────────────────────────────
  const yClient = isCaisseNoir ? 68 : 62;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...gold);
  doc.setLineWidth(0.5);
  doc.rect(14, yClient, 88, 32);
  doc.setFillColor(...gold);
  doc.rect(14, yClient, 88, 7, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont(undefined, 'bold');
  doc.text('CLIENT', 18, yClient + 5);

  doc.setTextColor(30, 30, 30);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(10);
  doc.text(vente.client_nom || '—', 18, yClient + 14);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  if (vente.client_telephone) doc.text(`Tel : ${vente.client_telephone}`, 18, yClient + 21);
  if (vente.client_email)     doc.text(`Email : ${vente.client_email}`, 18, yClient + 28);

  // ─── Bloc PAIEMENT ───────────────────────────────────────────────────────
  const modePaiementLabel = {
    'especes_caisse1': 'Especes - Caisse Officielle',
    'especes_caisse2': 'Especes - Caisse Noire',
    'virement':        'Virement Bancaire',
    'cheque':          'Cheque Bancaire',
    'carte':           'Carte Bancaire',
    'autre':           'Autre',
  };
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...gold);
  doc.rect(108, yClient, 88, 32);
  doc.setFillColor(...gold);
  doc.rect(108, yClient, 88, 7, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont(undefined, 'bold');
  doc.text('REGLEMENT', 112, yClient + 5);

  doc.setTextColor(30, 30, 30);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.text(`Mode : ${modePaiementLabel[vente.mode_paiement] || vente.mode_paiement || '—'}`, 112, yClient + 14);
  doc.text(`Boutique : ${vente.boutique_nom || '—'}`, 112, yClient + 21);
  doc.text(`Date vente : ${new Date(vente.date_vente).toLocaleDateString('fr-FR')}`, 112, yClient + 28);

  // ─── Titre section bijou ─────────────────────────────────────────────────
  let y = yClient + 40;
  doc.setFillColor(...dark);
  doc.rect(14, y, 182, 7, 'F');
  doc.setTextColor(...gold2);
  doc.setFontSize(9);
  doc.setFont(undefined, 'bold');
  doc.text('DETAIL DU BIJOU', 18, y + 5);
  y += 10;

  // ─── Infos bijou en grille 2 colonnes ────────────────────────────────────
  const nom     = bijou.nom || vente.bijou_nom || '—';
  const ref     = bijou.reference_article || bijou.reference || vente.reference || '—';
  const refUniq = bijou.reference_unique || bijou.id_produit || '—';
  const cat     = bijou.categorie || vente.categorie || '—';
  const couleur = bijou.couleur_or ? `Or ${bijou.couleur_or} 18 carats` : '—';
  const matiere = bijou.matiere ? bijou.matiere.toUpperCase() : '—';
  const poidsTotal = bijou.poids_reel || bijou.poids_final_sans_pierres || '—';
  const poidsPierres = bijou.poids_pierre_total && parseFloat(bijou.poids_pierre_total) > 0
    ? `${parseFloat(bijou.poids_pierre_total).toFixed(3)} g` : '—';
  const pierresDesc = bijou.pierres_desc || '—';
  const nbrePierres = bijou.nbre_pierre_total || '—';
  const taille      = bijou.taille || '—';
  const collection  = bijou.collection_nom || '—';
  const description = bijou.description && bijou.description !== bijou.reference_article ? bijou.description : null;

  const col1 = [
    ['Nom du bijou',    nom],
    ['Reference',       ref],
    ['ID produit',      refUniq],
    ['Categorie',       cat],
    ['Matiere',         matiere],
  ];
  const col2 = [
    ['Or / Couleur',    couleur],
    ['Poids total',     poidsTotal ? `${parseFloat(poidsTotal).toFixed(2)} g` : '—'],
    ['Poids pierres',   poidsPierres],
    ['Nb pierres',      String(nbrePierres)],
    ['Taille / Dim.',   taille],
  ];

  const rowH = 7;
  doc.setFillColor(255, 255, 255);
  doc.rect(14, y, 182, col1.length * rowH + 4, 'F');
  doc.setDrawColor(220, 210, 185);
  doc.setLineWidth(0.3);
  doc.rect(14, y, 182, col1.length * rowH + 4);

  col1.forEach((row, i) => {
    const ry = y + 4 + i * rowH;
    const bg = i % 2 === 0 ? [252, 250, 245] : [255, 255, 255];
    doc.setFillColor(...bg);
    doc.rect(14, ry - 3, 91, rowH, 'F');
    doc.rect(105, ry - 3, 91, rowH, 'F');
    doc.setTextColor(120, 100, 60);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(7.5);
    doc.text(row[0], 17, ry + 1);
    doc.text(col2[i][0], 108, ry + 1);
    doc.setTextColor(30, 30, 30);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8.5);
    doc.text(String(row[1]).substring(0, 38), 50, ry + 1);
    doc.text(String(col2[i][1]).substring(0, 38), 142, ry + 1);
  });

  y += col1.length * rowH + 8;

  // Pierres description (ligne entière si dispo)
  if (pierresDesc !== '—') {
    doc.setFillColor(248, 244, 232);
    doc.rect(14, y - 2, 182, 10, 'F');
    doc.setDrawColor(220, 210, 185);
    doc.rect(14, y - 2, 182, 10);
    doc.setTextColor(120, 100, 60);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(7.5);
    doc.text('Pierres :', 17, y + 4);
    doc.setTextColor(30, 30, 30);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    const pierresLines = doc.splitTextToSize(pierresDesc, 148);
    doc.text(pierresLines[0], 42, y + 4);
    y += 13;
  }

  // Collection
  if (collection !== '—') {
    doc.setFontSize(8);
    doc.setTextColor(100, 80, 40);
    doc.setFont(undefined, 'italic');
    doc.text(`Collection : ${collection}`, 17, y);
    y += 7;
  }

  // Description libre
  if (description) {
    doc.setFillColor(248, 244, 232);
    doc.rect(14, y, 182, 14, 'F');
    doc.setTextColor(50, 50, 50);
    doc.setFont(undefined, 'italic');
    doc.setFontSize(8);
    const descLines = doc.splitTextToSize(description, 176);
    doc.text(descLines.slice(0, 2), 17, y + 6);
    y += 17;
  }

  y += 3;

  // ─── Bloc TOTAL ──────────────────────────────────────────────────────────
  doc.setFillColor(...dark);
  doc.rect(108, y, 88, 22, 'F');
  doc.setFillColor(...gold);
  doc.rect(108, y + 22, 88, 1, 'F');
  doc.setTextColor(180, 180, 180);
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.text('MONTANT TOTAL', 152, y + 8, { align: 'center' });
  doc.setTextColor(...gold2);
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text(`${fmtPrix(vente.prix_vente)} DH`, 192, y + 18, { align: 'right' });

  // ─── Mentions légales ────────────────────────────────────────────────────
  y += 30;
  doc.setFontSize(7.5);
  doc.setFont(undefined, 'italic');
  if (isCaisseNoir) {
    doc.setTextColor(...accent);
    doc.text('Document non fiscal — Vente hors comptabilite officielle', 105, y, { align: 'center' });
  } else {
    doc.setTextColor(130, 130, 130);
    doc.text('Document fiscal — TVA non applicable (Art. 29B CGI) — Conserver ce document', 105, y, { align: 'center' });
  }

  // ─── Signature + Cachet ──────────────────────────────────────────────────
  y += 10;
  doc.setDrawColor(...gold);
  doc.setLineWidth(0.5);
  doc.setFillColor(255, 255, 255);
  doc.rect(20, y, 68, 28);
  doc.rect(122, y, 68, 28);
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(120, 100, 60);
  doc.text('Signature client', 54, y + 25, { align: 'center' });
  doc.text('Cachet boutique', 156, y + 25, { align: 'center' });

  // ─── Pied de page ────────────────────────────────────────────────────────
  doc.setFillColor(...dark);
  doc.rect(0, 283, 210, 14, 'F');
  doc.setFillColor(...gold);
  doc.rect(0, 283, 210, 1, 'F');
  doc.setTextColor(200, 200, 200);
  doc.setFontSize(7);
  doc.setFont(undefined, 'normal');
  doc.text('Bijouterie — Merci pour votre confiance', 105, 289, { align: 'center' });
  doc.text(`Facture N° ${String(vente.id).padStart(6, '0')} — Genere le ${new Date().toLocaleDateString('fr-FR')}`, 105, 294, { align: 'center' });

  const prefix = isCaisseNoir ? 'FACTURE_NOIR' : 'FACTURE_PREMIUM';
  doc.save(`${prefix}_${String(vente.id).padStart(6, '0')}_${(vente.client_nom || 'client').replace(/\s+/g, '_')}.pdf`);
};

// ═══════════════════════════════════════════════════════════════════════════
// FICHE DE FABRICATION — Document pour l'ouvrier à l'atelier
// ═══════════════════════════════════════════════════════════════════════════
export const generateFicheFabricationPDF = (ordre, bijou = {}, etapes = [], composants = [], pierres = []) => {
  const doc = new jsPDF();
  const gold = [180, 140, 50];
  const dark = [30, 30, 30];

  // En-tête
  doc.setFillColor(...dark);
  doc.rect(0, 0, 210, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.text('FICHE DE FABRICATION', 105, 18, { align: 'center' });
  doc.setFontSize(12);
  doc.setFont(undefined, 'normal');
  doc.text(`${ordre.numero_ordre || 'OF-??????'}`, 105, 28, { align: 'center' });
  doc.setFontSize(9);
  doc.text(`Imprime le ${new Date().toLocaleDateString('fr-FR')}`, 105, 35, { align: 'center' });

  // Infos bijou + photo
  let y = 50;
  doc.setTextColor(0, 0, 0);
  const imageUrl = bijou.image_url || ordre.image_url;
  const hasImage = !!imageUrl;

  // Bloc infos (gauche si photo, sinon pleine largeur)
  doc.setFillColor(245, 245, 245);
  doc.rect(15, y, hasImage ? 125 : 180, 40, 'F');
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text(`Ref : ${bijou.reference_article || ordre.ref_article || '—'}`, 20, y + 8);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.text(`ID : ${bijou.id_produit || '—'}`, 20, y + 15);
  doc.text(`Type : ${ordre.type_bijou || bijou.categorie || '—'}`, 20, y + 22);
  doc.text(`Or : ${ordre.couleur_or || bijou.couleur_or || '—'}`, 20, y + 29);
  doc.text(`Collection : ${bijou.collection_nom || '—'}`, 20, y + 36);
  doc.text(`Client : ${ordre.client_nom || bijou.client_nom || 'Bijouterie'}`, 85, y + 15);
  if (bijou.description && bijou.description !== bijou.reference_article) {
    doc.text(`${bijou.description.substring(0, 35)}`, 85, y + 22);
  }

  // Photo à droite (si disponible)
  if (hasImage) {
    try {
      doc.addImage(imageUrl, 'JPEG', 142, y, 50, 40, undefined, 'FAST');
    } catch (e) {
      // Si l'image ne peut pas être ajoutée (CORS etc.) — cadre vide
      doc.setFillColor(240, 240, 240);
      doc.rect(142, y, 50, 40, 'F');
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text('Photo', 167, y + 22, { align: 'center' });
      doc.setTextColor(0, 0, 0);
    }
  }

  y += 48;

  // Composants fabrication
  if (composants.length > 0) {
    doc.setFillColor(...gold);
    doc.rect(15, y, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('COMPOSANTS DE FABRICATION', 20, y + 6);
    y += 10;

    autoTable(doc, {
      startY: y,
      head: [['Composant', 'Quantite', 'Prix unit.', 'Total']],
      body: composants.map(c => [
        c.nom || '—',
        String(c.quantite || 1),
        `${parseFloat(c.prix_unitaire || c.cout || 0).toFixed(2)} DH`,
        `${parseFloat(c.cout_total || 0).toFixed(2)} DH`,
      ]),
      theme: 'grid',
      headStyles: { fillColor: [...dark], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // Pierres
  if (pierres.length > 0) {
    doc.setFillColor(80, 80, 180);
    doc.rect(15, y, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('PIERRES', 20, y + 6);
    y += 10;

    autoTable(doc, {
      startY: y,
      head: [['Type', 'Designation', 'Qte', 'Carats']],
      body: pierres.map(p => [
        p.type_pierre || '—',
        p.designation || p.reference_pierre || '—',
        String(p.quantite || 1),
        `${parseFloat(p.poids_carats || 0).toFixed(2)} ct`,
      ]),
      theme: 'grid',
      headStyles: { fillColor: [80, 80, 180], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // Étapes de fabrication
  if (etapes.length > 0) {
    doc.setFillColor(40, 120, 60);
    doc.rect(15, y, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('ETAPES DE FABRICATION', 20, y + 6);
    y += 10;

    autoTable(doc, {
      startY: y,
      head: [['#', 'Etape', 'Ouvrier', 'Temps estime', 'Temps reel', 'Statut']],
      body: etapes.map((e, i) => [
        String(i + 1),
        e.nom_etape || e.nom || e.mission || '—',
        e.ouvrier_nom || '—',
        e.duree_estimee_heures ? `${e.duree_estimee_heures}h` : '—',
        e.duree_reelle_heures ? `${parseFloat(e.duree_reelle_heures).toFixed(1)}h` : '—',
        e.statut === 'terminee' ? 'FAIT' : e.statut === 'en_cours' ? 'EN COURS' : 'A FAIRE',
      ]),
      theme: 'grid',
      headStyles: { fillColor: [40, 120, 60], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // Zone poids — à remplir à la main
  if (y < 220) {
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.rect(15, y + 5, 180, 40);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('PESEE (a remplir par l\'ouvrier)', 20, y + 13);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.text('Or donne : .............. g', 20, y + 22);
    doc.text('Poids bijou fini : .............. g', 20, y + 30);
    doc.text('Or rendu : .............. g', 120, y + 22);
    doc.text('Temps total : .............. min', 120, y + 30);
    doc.text('Signature ouvrier : ..............................', 20, y + 38);
  }

  // Pied de page
  doc.setFillColor(...dark);
  doc.rect(0, 285, 210, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.text(`Fiche fabrication ${ordre.numero_ordre || ''} - ${bijou.reference_article || ''}`, 105, 291, { align: 'center' });

  doc.save(`FICHE_FAB_${ordre.numero_ordre || 'OF'}_${bijou.reference_article || ''}.pdf`);
};


// ═══════════════════════════════════════════════════════════════════════════
// FICHE PIÈCE INDIVIDUELLE — Une fiche par pièce dans un OF multi-bijoux
// ═══════════════════════════════════════════════════════════════════════════
export const generateFichePiecePDF = (ordre, piece, composants = [], pierres = [], etapes = []) => {
  const doc = new jsPDF();
  const gold = [180, 140, 50];
  const dark = [30, 30, 30];

  // Poids unitaire : priorité poids_unitaire de l'OF, sinon poids_metal / quantite_pieces
  const qte = parseInt(ordre.quantite_pieces || 1);
  const poidsTotal = parseFloat(ordre.poids_metal || ordre.poids_total_or || 0);
  const poidsUnitaire = parseFloat(ordre.poids_unitaire || 0) || (qte > 0 ? poidsTotal / qte : 0);

  // Couleur de la pièce (peut différer de l'OF si modifiée)
  const couleurPiece = piece.couleur_or || ordre.couleur_or || '—';
  const ouvrierPiece = piece.ouvrier_nom || '—';
  const refPiece = piece.reference_piece || `${ordre.numero_ordre}-${piece.numero_piece}`;

  // ── En-tête ──
  doc.setFillColor(...dark);
  doc.rect(0, 0, 210, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('FICHE DE FABRICATION', 105, 16, { align: 'center' });
  doc.setFontSize(13);
  doc.text(refPiece, 105, 27, { align: 'center' });
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.text(`Pièce ${piece.numero_piece} / ${qte}   —   Imprimé le ${new Date().toLocaleDateString('fr-FR')}`, 105, 35, { align: 'center' });

  // ── Note importante ──
  let y = 50;
  if (ordre.notes) {
    doc.setFillColor(255, 235, 235);
    doc.rect(15, y, 180, 12, 'F');
    doc.setTextColor(180, 0, 0);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text(`/!\\ NOTE : ${ordre.notes.substring(0, 90)}`, 20, y + 8);
    doc.setTextColor(0, 0, 0);
    y += 16;
  }

  // ── Bloc infos bijou ──
  doc.setFillColor(245, 245, 245);
  doc.rect(15, y, 180, 46, 'F');
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(`Réf : ${ordre.ref_article || '—'}`, 20, y + 8);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);

  // Colonne gauche
  doc.text(`Type : ${ordre.type_bijou || '—'}`, 20, y + 16);
  doc.text(`Or : ${couleurPiece}`, 20, y + 23);
  doc.text(`Genre : ${ordre.genre || '—'}`, 20, y + 30);
  doc.text(`Collection : ${ordre.collection_nom || ordre.client_nom || 'Bijouterie'}`, 20, y + 37);

  // Colonne droite — poids UNITAIRE (pas total OF)
  doc.setFont(undefined, 'bold');
  doc.setFontSize(13);
  doc.setTextColor(160, 100, 0);
  doc.text(`${poidsUnitaire > 0 ? poidsUnitaire.toFixed(3) : '—'} g`, 165, y + 18, { align: 'right' });
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('Poids OR brut', 165, y + 25, { align: 'right' });
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.text(`Ouvrier : ${ouvrierPiece}`, 115, y + 35);

  if (ordre.description) {
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.text(`${ordre.description.substring(0, 50)}`, 115, y + 43);
    doc.setTextColor(0, 0, 0);
  }

  // Options finition
  const opts = [];
  if (ordre.rhodiage) opts.push('Rhodiage');
  if (piece.couleur_or === 'BLANC' || piece.couleur_or === 'ROSE') opts.push('→ Rhodiage selon couleur');
  if (ordre.ecrin) opts.push('Écrin');
  if (ordre.gravure_beldi) opts.push('Gravure beldi');
  if (ordre.garantie) opts.push('Garantie');
  if (ordre.fonte2) opts.push('2ème fonte');
  if (opts.length > 0) {
    doc.setFontSize(8);
    doc.setTextColor(50, 100, 50);
    doc.text(`Options : ${opts.join(' | ')}`, 20, y + 44);
    doc.setTextColor(0, 0, 0);
  }

  y += 52;

  // ── Composants fabrication ──
  if (composants.length > 0) {
    doc.setFillColor(...gold);
    doc.rect(15, y, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('COMPOSANTS DE FABRICATION', 20, y + 6);
    y += 10;

    autoTable(doc, {
      startY: y,
      head: [['Composant', 'Qté', 'Prix unit.', 'Total']],
      body: composants.map(c => [
        c.nom || '—',
        String(c.quantite || 1),
        `${parseFloat(c.prix_unitaire || c.cout || 0).toFixed(2)} DH`,
        `${parseFloat(c.cout_total || 0).toFixed(2)} DH`,
      ]),
      theme: 'grid',
      headStyles: { fillColor: [...dark], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // ── Pierres (divisées par quantite_pieces pour la pièce individuelle) ──
  if (pierres.length > 0) {
    doc.setFillColor(80, 80, 180);
    doc.rect(15, y, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('PIERRES', 20, y + 6);
    y += 10;

    autoTable(doc, {
      startY: y,
      head: [['Type', 'Désignation', 'Qté/pièce', 'Carats/pièce']],
      body: pierres.map(p => {
        const qteP = Math.ceil((p.quantite || 1) / qte);
        const ctsP = qte > 0 ? parseFloat(p.poids_carats || 0) / qte : parseFloat(p.poids_carats || 0);
        return [
          p.type_pierre || '—',
          p.designation || p.reference_pierre || '—',
          String(qteP),
          `${ctsP.toFixed(3)} ct`,
        ];
      }),
      theme: 'grid',
      headStyles: { fillColor: [80, 80, 180], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // ── Étapes de fabrication (de cette pièce, ou de l'OF si pas de pièce spécifique) ──
  if (etapes.length > 0) {
    doc.setFillColor(40, 120, 60);
    doc.rect(15, y, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('ÉTAPES DE FABRICATION', 20, y + 6);
    y += 10;

    autoTable(doc, {
      startY: y,
      head: [['#', 'Étape', 'Ouvrier', 'Tps estimé', 'Statut']],
      body: etapes.map((e, i) => [
        String(i + 1),
        e.nom_etape || e.nom || '—',
        e.ouvrier_nom || ouvrierPiece,
        e.duree_estimee_heures ? `${e.duree_estimee_heures}h` : '—',
        e.statut === 'terminee' ? '✓ FAIT' : e.statut === 'en_cours' ? '⟳ EN COURS' : '○ À FAIRE',
      ]),
      theme: 'grid',
      headStyles: { fillColor: [40, 120, 60], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // ── Zone pesée à remplir par l'ouvrier ──
  const yZone = Math.max(y + 3, 220);
  if (yZone < 260) {
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.rect(15, yZone, 180, 44, 'F');
    doc.setFillColor(255, 255, 255);
    doc.rect(15, yZone, 180, 44, 'F');
    doc.rect(15, yZone, 180, 44);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('PESÉE (à remplir par l\'ouvrier)', 20, yZone + 9);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.text(`Or donné : ${poidsUnitaire > 0 ? poidsUnitaire.toFixed(3) + ' g' : '.............. g'}`, 20, yZone + 19);
    doc.text('Poids bijou fini : .............. g', 20, yZone + 27);
    doc.text('Or rendu : .............. g', 115, yZone + 19);
    doc.text('Temps total : .............. min', 115, yZone + 27);
    doc.text('Signature ouvrier : ..............................', 20, yZone + 38);
    doc.text(`Date : ....../....../......`, 130, yZone + 38);
  }

  // ── Pied de page ──
  doc.setFillColor(...dark);
  doc.rect(0, 285, 210, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.text(`${refPiece} — ${ordre.type_bijou || ''} ${couleurPiece} — Pièce ${piece.numero_piece}/${qte} — ${poidsUnitaire > 0 ? poidsUnitaire.toFixed(3) + 'g' : ''}`, 105, 291, { align: 'center' });

  doc.save(`FICHE_${refPiece.replace(/[^a-zA-Z0-9-]/g, '_')}.pdf`);
};


// ═══════════════════════════════════════════════════════════════════════════
// FICHE GÉNÉRALE DE PRODUCTION — Résumé pour le chef d'atelier
// Montre tous les bijoux d'un même ordre avec leur référence individuelle
// ═══════════════════════════════════════════════════════════════════════════
export const generateFicheGeneralePDF = (ordre, enfants = [], etapes = []) => {
  const doc = new jsPDF();
  const gold  = [180, 140, 50];
  const dark  = [30, 30, 30];
  const green = [40, 120, 60];

  const qte          = parseInt(ordre.quantite_pieces || enfants.length || 1);
  const poidsTotal   = parseFloat(ordre.poids_metal || ordre.poids_total_or || 0);
  const poidsUnit    = parseFloat(ordre.poids_unitaire || 0) || (qte > 0 ? poidsTotal / qte : 0);
  const couleur      = (ordre.couleur_or || 'JAUNE').toUpperCase();
  const refArticle   = ordre.ref_article || ordre.numero_ordre || '—';
  const dateImprime  = new Date().toLocaleDateString('fr-FR');

  // ── En-tête ──────────────────────────────────────────────────────────────
  doc.setFillColor(...dark);
  doc.rect(0, 0, 210, 44, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.text('ORDRE DE FABRICATION', 105, 15, { align: 'center' });
  doc.setFontSize(14);
  doc.text(`${ordre.numero_ordre}  —  ${ordre.type_bijou || 'BIJOU'} × ${qte}`, 105, 27, { align: 'center' });
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.text(`Réf : ${refArticle}   |   Imprimé le ${dateImprime}`, 105, 37, { align: 'center' });

  // ── Bloc infos générales ──────────────────────────────────────────────────
  let y = 52;
  doc.setFillColor(245, 245, 245);
  doc.rect(15, y, 180, 32, 'F');
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');

  // Colonne gauche
  doc.setFont(undefined, 'bold'); doc.text('Type bijou :', 20, y + 8); doc.setFont(undefined, 'normal');
  doc.text(ordre.type_bijou || '—', 55, y + 8);
  doc.setFont(undefined, 'bold'); doc.text('Or / Couleur :', 20, y + 16); doc.setFont(undefined, 'normal');
  doc.text(couleur, 55, y + 16);
  doc.setFont(undefined, 'bold'); doc.text('Collection :', 20, y + 24); doc.setFont(undefined, 'normal');
  doc.text(ordre.collection_nom || ordre.client_nom || 'Bijouterie', 55, y + 24);

  // Colonne droite
  doc.setFont(undefined, 'bold'); doc.text('Quantité totale :', 115, y + 8); doc.setFont(undefined, 'normal');
  doc.text(`${qte} bijoux`, 160, y + 8);
  doc.setFont(undefined, 'bold'); doc.text('Poids OR / bijou :', 115, y + 16); doc.setFont(undefined, 'normal');
  doc.setTextColor(160, 100, 0);
  doc.text(`${poidsUnit > 0 ? poidsUnit.toFixed(3) : '—'} g`, 165, y + 16, { align: 'right' });
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'bold'); doc.text('Poids OR total :', 115, y + 24); doc.setFont(undefined, 'normal');
  doc.text(`${poidsTotal > 0 ? poidsTotal.toFixed(3) : '—'} g`, 165, y + 24, { align: 'right' });

  y += 38;

  // ── Note si présente ──────────────────────────────────────────────────────
  if (ordre.notes) {
    doc.setFillColor(255, 235, 235);
    doc.rect(15, y, 180, 10, 'F');
    doc.setTextColor(180, 0, 0);
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.text(`/!\\${ordre.notes.substring(0, 100)}`, 20, y + 7);
    doc.setTextColor(0, 0, 0);
    y += 14;
  }

  // ── Tableau des bijoux (1 ligne par child OF) ─────────────────────────────
  doc.setFillColor(...gold);
  doc.rect(15, y, 180, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text(`BIJOUX DE CET ORDRE (${qte})`, 20, y + 6);
  y += 10;

  const statutLabel = (s) => {
    if (!s) return 'En attente';
    if (s === 'terminé' || s === 'termine' || s === 'terminee') return '✓ Terminé';
    if (s === 'en_cours') return '⟳ En cours';
    if (s === 'en_attente') return '○ En attente';
    return s;
  };

  autoTable(doc, {
    startY: y,
    head: [['#', 'Référence bijou', 'Couleur / Or', 'Ouvrier assigné', 'Statut', 'Avancement']],
    body: enfants.length > 0
      ? enfants.map((e, i) => {
          const nbEt  = parseInt(e.nb_etapes || 0);
          const nbDone = parseInt(e.etapes_terminees || 0);
          const pct   = nbEt > 0 ? Math.round(nbDone / nbEt * 100) : 0;
          return [
            String(e.piece_index || i + 1),
            e.numero_ordre || e.reference_piece || `—`,
            (e.couleur_or || couleur),
            e.artisan_nom || e.ouvrier_nom || '—',
            statutLabel(e.statut),
            nbEt > 0 ? `${nbDone}/${nbEt} (${pct}%)` : '—',
          ];
        })
      : Array.from({ length: qte }, (_, i) => [
          String(i + 1),
          `${ordre.numero_ordre}-${i + 1}`,
          couleur, '—', 'En attente', '—',
        ]),
    theme: 'grid',
    headStyles: { fillColor: [...dark], fontSize: 9, fontStyle: 'bold' },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 42 }, 4: { cellWidth: 28 }, 5: { cellWidth: 32 } },
  });
  y = doc.lastAutoTable.finalY + 6;

  // ── Étapes communes ───────────────────────────────────────────────────────
  if (etapes.length > 0 && y < 240) {
    doc.setFillColor(...green);
    doc.rect(15, y, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('ÉTAPES DE FABRICATION (communes à tous les bijoux)', 20, y + 6);
    y += 10;

    autoTable(doc, {
      startY: y,
      head: [['#', 'Étape', 'Tps estimé']],
      body: etapes.map((e, i) => [
        String(i + 1),
        e.nom_etape || e.nom || '—',
        e.duree_estimee_heures ? `${e.duree_estimee_heures}h` : '—',
      ]),
      theme: 'grid',
      headStyles: { fillColor: [...green], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { cellWidth: 10 }, 2: { cellWidth: 22 } },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // ── Zone validation chef d'atelier ────────────────────────────────────────
  if (y < 255) {
    doc.setDrawColor(0);
    doc.setLineWidth(0.4);
    doc.rect(15, y, 180, 22);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text('Validation chef d\'atelier :', 20, y + 8);
    doc.setFont(undefined, 'normal');
    doc.text('Signature : ......................................', 20, y + 17);
    doc.text('Date : ....../....../......', 130, y + 17);
  }

  // ── Pied de page ──────────────────────────────────────────────────────────
  doc.setFillColor(...dark);
  doc.rect(0, 285, 210, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.text(
    `${ordre.numero_ordre} — ${ordre.type_bijou || ''} ${couleur} — ${qte} bijoux — Or total : ${poidsTotal > 0 ? poidsTotal.toFixed(3) + 'g' : '—'}`,
    105, 291, { align: 'center' }
  );

  doc.save(`OF_GENERAL_${(ordre.numero_ordre || 'OF').replace(/[^a-zA-Z0-9-]/g, '_')}.pdf`);
};


// ═══════════════════════════════════════════════════════════════════════════
// ÉTIQUETTE BIJOU — Petite étiquette à coller/attacher au bijou
// ═══════════════════════════════════════════════════════════════════════════
export const generateEtiquetteBijouPDF = (bijou, options = {}) => {
  // Format étiquette : 80mm x 40mm (landscape)
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [40, 80] });

  const ref = bijou.reference_article || bijou.reference || '—';
  const idp = bijou.id_produit || '';
  const prix = bijou.pvp_magasin || bijou.prix_vente || 0;
  const couleur = bijou.couleur_or || '';
  const poids = bijou.poids_metal || bijou.poids_reel || bijou.poids_final_sans_pierres || '';
  const collection = bijou.collection_nom || '';

  // Cadre
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.rect(1, 1, 78, 38);

  // Référence (gros)
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text(ref, 40, 8, { align: 'center' });

  // ID produit
  doc.setFontSize(7);
  doc.setFont(undefined, 'normal');
  doc.text(idp, 40, 12, { align: 'center' });

  // Ligne séparatrice
  doc.setLineWidth(0.2);
  doc.line(5, 14, 75, 14);

  // Détails
  doc.setFontSize(8);
  if (couleur) doc.text(`Or ${couleur.toLowerCase()} 18k`, 5, 19);
  if (poids) doc.text(`${parseFloat(poids).toFixed(2)}g`, 55, 19);
  if (collection) doc.text(collection, 5, 24);

  // Prix (gros en bas)
  if (prix > 0 && !options.hidePrix) {
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`${fmtPrix(prix)} DH`, 40, 33, { align: 'center' });
  }

  // Code sous le prix
  doc.setFontSize(6);
  doc.setFont(undefined, 'normal');
  doc.text(`${ref} | ${idp}`, 40, 37, { align: 'center' });

  doc.save(`ETIQUETTE_${ref}_${idp}.pdf`);
};


// ═══════════════════════════════════════════════════════════════════════════
// ÉTIQUETTES EN LOT — Plusieurs étiquettes sur une page A4
// ═══════════════════════════════════════════════════════════════════════════
export const generateEtiquettesLotPDF = (bijoux) => {
  const doc = new jsPDF();
  const cols = 3;
  const rows = 7;
  const w = 60;
  const h = 38;
  const mx = 10;
  const my = 8;
  let idx = 0;

  bijoux.forEach((bijou, i) => {
    if (i > 0 && i % (cols * rows) === 0) doc.addPage();
    const pos = i % (cols * rows);
    const col = pos % cols;
    const row = Math.floor(pos / cols);
    const x = mx + col * (w + 3);
    const y = my + row * (h + 2);

    const ref = bijou.reference_article || bijou.reference || '—';
    const idp = bijou.id_produit || '';
    const prix = bijou.pvp_magasin || bijou.prix_vente || 0;
    const couleur = bijou.couleur_or || '';
    const poids = bijou.poids_metal || bijou.poids_reel || '';

    doc.setDrawColor(180);
    doc.setLineWidth(0.2);
    doc.rect(x, y, w, h);

    doc.setTextColor(0);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text(ref, x + w/2, y + 7, { align: 'center' });

    doc.setFontSize(6);
    doc.setFont(undefined, 'normal');
    doc.text(idp, x + w/2, y + 11, { align: 'center' });

    doc.setLineWidth(0.1);
    doc.line(x + 3, y + 13, x + w - 3, y + 13);

    doc.setFontSize(7);
    if (couleur) doc.text(`Or ${couleur.toLowerCase()} 18k`, x + 3, y + 18);
    if (poids) doc.text(`${parseFloat(poids).toFixed(2)}g`, x + w - 3, y + 18, { align: 'right' });

    if (prix > 0) {
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text(`${fmtPrix(prix)} DH`, x + w/2, y + 30, { align: 'center' });
    }

    doc.setFontSize(5);
    doc.setFont(undefined, 'normal');
    doc.text(`${ref} | ${idp}`, x + w/2, y + 36, { align: 'center' });
  });

  doc.save(`ETIQUETTES_LOT_${new Date().toISOString().split('T')[0]}.pdf`);
};


export const generateDevisPDF = async (devis, couts = {}) => {
  const doc = new jsPDF();
  const nom = await getNomEntreprise();
  const gold = [212, 175, 55];
  const dark = [44, 62, 80];

  // En-tête
  doc.setFillColor(...dark);
  doc.rect(0, 0, 210, 48, 'F');
  doc.setFillColor(...gold);
  doc.rect(0, 48, 210, 3, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont(undefined, 'bold');
  doc.text(nom.toUpperCase(), 105, 18, { align: 'center' });
  doc.setFontSize(14);
  doc.text('DEVIS', 105, 30, { align: 'center' });
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  const ref = devis.reference_devis || devis.reference_article || `DEV-${String(devis.id).padStart(5, '0')}`;
  doc.text(`Ref: ${ref}`, 105, 40, { align: 'center' });

  let y = 60;
  doc.setTextColor(0, 0, 0);

  // Infos devis
  doc.setFillColor(245, 245, 245);
  doc.rect(15, y, 85, 35, 'F');
  doc.rect(110, y, 85, 35, 'F');

  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('DEVIS', 20, y + 10);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.text(`Date: ${new Date(devis.date_creation || Date.now()).toLocaleDateString('fr-FR')}`, 20, y + 18);
  doc.text(`Type: ${devis.type_bijou || ''}`, 20, y + 24);
  doc.text(`Or: ${devis.couleur_or || ''}`, 20, y + 30);

  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('CLIENT', 115, y + 10);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.text(devis.client_nom || 'Non specifie', 115, y + 18);
  if (devis.date_livraison_souhaitee) {
    doc.text(`Livraison souhaitee: ${new Date(devis.date_livraison_souhaitee).toLocaleDateString('fr-FR')}`, 115, y + 24);
  }
  if (devis.type_creation) {
    doc.text(`Commande: ${devis.type_creation}`, 115, y + 30);
  }

  y += 45;

  // Description
  if (devis.description) {
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Description:', 15, y);
    doc.setFont(undefined, 'normal');
    const lines = doc.splitTextToSize(devis.description, 180);
    doc.text(lines, 15, y + 6);
    y += 6 + lines.length * 5;
  }

  y += 5;

  // Tableau des coûts
  const lignes = [];
  const coutMetal = parseFloat(couts.cout_metal || devis.cout_metal || 0);
  const coutPierres = parseFloat(couts.cout_pierres || devis.cout_pierres || 0);
  const coutMO = parseFloat(couts.cout_main_oeuvre || devis.cout_main_oeuvre || 0);
  const coutFab = parseFloat(couts.cout_fabrication || devis.cout_fabrication || 0);
  const coutPack = parseFloat(couts.cout_packaging || devis.cout_packaging || 0);

  if (coutMetal > 0) lignes.push(['Metal precieux (Or ' + (devis.couleur_or || '18k') + ')', coutMetal.toFixed(2) + ' MAD']);
  if (coutPierres > 0) lignes.push(['Pierres precieuses', coutPierres.toFixed(2) + ' MAD']);
  if (coutMO > 0) lignes.push(["Main d'oeuvre", coutMO.toFixed(2) + ' MAD']);
  if (coutFab > 0) lignes.push(['Couts de fabrication', coutFab.toFixed(2) + ' MAD']);
  if (coutPack > 0) lignes.push(['Packaging / Ecrin', coutPack.toFixed(2) + ' MAD']);

  if (lignes.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [['Poste', 'Montant']],
      body: lignes,
      theme: 'grid',
      headStyles: { fillColor: dark, textColor: [255, 255, 255], fontStyle: 'bold' },
      styles: { fontSize: 10 },
      columnStyles: { 1: { halign: 'right' } },
      margin: { left: 15, right: 15 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Totaux
  const prixRevient = coutMetal + coutPierres + coutMO + coutFab + coutPack;
  const coeff = parseFloat(devis.coefficient_marge || 1.5);
  const prixVente = parseFloat(devis.prix_vente_final || devis.prix_vente || (prixRevient * coeff));

  doc.setFillColor(245, 245, 245);
  doc.rect(110, y, 85, 30, 'F');
  doc.setFontSize(10);
  doc.text('Prix de revient:', 115, y + 8);
  doc.text(`${prixRevient.toFixed(2)} MAD`, 190, y + 8, { align: 'right' });
  doc.text(`Coefficient: x${coeff}`, 115, y + 16);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...gold);
  doc.text('Prix de vente:', 115, y + 26);
  doc.text(`${prixVente.toFixed(2)} MAD`, 190, y + 26, { align: 'right' });

  y += 40;
  doc.setTextColor(0, 0, 0);

  // Conditions
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text('Ce devis est valable 30 jours a compter de sa date de creation.', 15, y);
  doc.text("Un acompte de 50% est demande a la commande, le solde a la livraison.", 15, y + 5);
  doc.text("Les delais de fabrication sont donnes a titre indicatif et peuvent varier.", 15, y + 10);

  // Pied de page
  doc.setFillColor(...dark);
  doc.rect(0, 282, 210, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.text(`${nom} - Document genere le ${new Date().toLocaleDateString('fr-FR')}`, 105, 290, { align: 'center' });

  doc.save(`DEVIS_${ref}_${new Date().toISOString().split('T')[0]}.pdf`);
};

// Export
export default {
  generateFacturePDF,
  generateFacturePremiumPDF,
  generateFicheFabricationPDF,
  generateEtiquetteBijouPDF,
  generateEtiquettesLotPDF,
  generateDevisPDF,
};
